import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { adminService } from '../services/api';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Users, Zap, MessageSquare, LogOut, Terminal, FolderDot, Calendar, Search, Filter } from 'lucide-react';

const Dashboard = () => {
  const { logout } = useContext(AuthContext);
  const [stats, setStats] = useState({ totalUsers: 0, proUsers: 0, openFeedbacks: 0 });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // New Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchStats = async (isBackgroundPing = false) => {
    try {
      const { data } = await adminService.getStats();
      setStats((prevStats) => {
        if (isBackgroundPing && data.stats.openFeedbacks > prevStats.openFeedbacks) {
          toast('New Support Ticket Arrived!', { icon: '🚨', style: { background: '#059669', color: '#fff' } });
        }
        return data.stats;
      });
      if (data.users) setUsers(data.users);
    } catch (error) {
      if (!isBackgroundPing) toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats(false);
    const interval = setInterval(() => fetchStats(true), 300000); 
    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Filter Logic
  const filteredUsers = users.filter((user) => {
    const matchesSearch = 
      (user.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      statusFilter === 'all' || 
      (statusFilter === 'pro' && user.subscriptionStatus === 'active') ||
      (statusFilter === 'free' && user.subscriptionStatus !== 'active');
      
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-sans selection:bg-green-500/30 pb-12">
      {/* Navigation Bar */}
      <nav className="border-b border-gray-800 bg-[#111111]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Terminal className="text-green-500 w-6 h-6" />
            <span className="font-bold text-lg tracking-tight">EnvDock <span className="text-green-500">Admin</span></span>
          </div>
          <div className="flex gap-4 items-center">
            <Link to="/feedbacks" className="text-sm font-medium text-gray-400 hover:text-white transition-colors">Tickets</Link>
            <button onClick={logout} className="text-gray-400 hover:text-red-400 transition-colors p-2 bg-gray-900 rounded-lg border border-gray-800 hover:border-red-900">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header & Stats Section */}
        <div className="mb-10">
          <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Zap className="w-5 h-5 text-gray-400" /> System Overview
          </h1>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-pulse">
              <div className="h-32 bg-gray-900 border border-gray-800 rounded-xl"></div>
              <div className="h-32 bg-gray-900 border border-gray-800 rounded-xl"></div>
              <div className="h-32 bg-gray-900 border border-gray-800 rounded-xl"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              <div className="bg-[#111111] border border-gray-800 rounded-xl p-6 shadow-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Total Developers</p>
                    <p className="text-3xl font-bold mt-2 font-mono">{stats.totalUsers}</p>
                  </div>
                  <div className="p-3 bg-blue-500/10 rounded-lg"><Users className="text-blue-500 w-5 h-5" /></div>
                </div>
              </div>

              <div className="bg-[#111111] border border-gray-800 rounded-xl p-6 relative overflow-hidden shadow-sm">
                <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-full blur-3xl pointer-events-none"></div>
                <div className="flex justify-between items-start relative z-10">
                  <div>
                    <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Pro Subscribers</p>
                    <p className="text-3xl font-bold mt-2 text-green-400 font-mono">{stats.proUsers}</p>
                  </div>
                  <div className="p-3 bg-green-500/10 rounded-lg"><Zap className="text-green-500 w-5 h-5" /></div>
                </div>
              </div>

              <Link to="/feedbacks" className="bg-[#111111] border border-gray-800 hover:border-gray-600 transition-colors rounded-xl p-6 flex flex-col justify-between cursor-pointer group shadow-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Open Tickets</p>
                    <p className="text-3xl font-bold mt-2 text-red-400 font-mono group-hover:text-red-300">{stats.openFeedbacks}</p>
                  </div>
                  <div className="p-3 bg-red-500/10 rounded-lg"><MessageSquare className="text-red-500 w-5 h-5" /></div>
                </div>
              </Link>
            </div>
          )}
        </div>

        {/* Directory Section with Filters */}
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Users className="w-5 h-5 text-gray-400" /> Recent Registrations
            </h2>
            
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input 
                  type="text" 
                  placeholder="Search name or email..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-[#111111] border border-gray-800 text-sm text-gray-200 rounded-lg pl-9 pr-4 py-2 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500/50 w-full sm:w-64 transition-all"
                />
              </div>
              <div className="relative">
                <Filter className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <select 
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="bg-[#111111] border border-gray-800 text-sm text-gray-200 rounded-lg pl-9 pr-8 py-2 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500/50 appearance-none w-full sm:w-auto transition-all cursor-pointer"
                >
                  <option value="all">All Plans</option>
                  <option value="pro">Pro Only</option>
                  <option value="free">Free Only</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="bg-[#111111] border border-gray-800 rounded-xl overflow-hidden shadow-sm">
            
            {/* MOBILE VIEW: Stacked Cards (Hidden on md screens and up) */}
            <div className="md:hidden flex flex-col divide-y divide-gray-800/50">
              {loading ? (
                <div className="p-8 text-center text-gray-500 font-mono text-xs">Loading directory...</div>
              ) : filteredUsers.length === 0 ? (
                <div className="p-8 text-center text-gray-500 font-mono text-xs">No users match your criteria.</div>
              ) : (
                filteredUsers.map((user) => (
                  <div key={user._id} className="p-4 flex flex-col gap-4 hover:bg-gray-800/20 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-sm font-bold text-gray-400 border border-gray-700">
                          {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div>
                          <p className="font-medium text-white">{user.name || 'Unknown User'}</p>
                          <p className="text-xs text-gray-500 truncate w-40 sm:w-auto">{user.email}</p>
                        </div>
                      </div>
                      <div>
                        {user.subscriptionStatus === 'active' ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-green-500/10 text-green-400 border border-green-500/20">
                            <Zap className="w-3 h-3" /> Pro
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-gray-800 text-gray-400 border border-gray-700">
                            Free
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center text-xs text-gray-400 bg-[#0a0a0a] p-3 rounded-lg border border-gray-800/60">
                      <div className="flex items-center gap-2 font-mono">
                        <FolderDot className="w-4 h-4 text-gray-500" />
                        <span>{user.projectCount || 0} <span className="text-gray-600">Proj</span></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        {formatDate(user.createdAt)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* DESKTOP VIEW: Table (Hidden on small screens) */}
            <div className="hidden md:block">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-[#0a0a0a] text-gray-400 border-b border-gray-800 uppercase text-xs font-semibold tracking-wider">
                  <tr>
                    <th className="px-6 py-4">Developer</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4 text-center">Projects</th>
                    <th className="px-6 py-4">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/50 text-gray-300">
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-gray-500 font-mono text-xs">Loading directory...</td>
                    </tr>
                  ) : filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-gray-500 font-mono text-xs">No users match your criteria.</td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user._id} className="hover:bg-gray-800/20 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center text-xs font-bold text-gray-400 border border-gray-700">
                              {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                            </div>
                            <div>
                              <p className="font-medium text-white">{user.name || 'Unknown User'}</p>
                              <p className="text-xs text-gray-500">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {user.subscriptionStatus === 'active' ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20">
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span> Pro
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-800 text-gray-400 border border-gray-700">
                              Free
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-1 text-gray-400 font-mono">
                            <FolderDot className="w-4 h-4" />
                            {user.projectCount || 0}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-gray-400">
                            <Calendar className="w-4 h-4" />
                            {formatDate(user.createdAt)}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </main>
    </div>
  );
};

export default Dashboard;